import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatCost, formatNumber, formatTimestamp } from '@/lib/formatters';
import type { AdminUserCostAuditResponse } from '@/types/admin';

function DiscrepancyBadge({ value }: { value: number }) {
  const abs = Math.abs(value);
  if (abs < 0.0001) return <Badge variant="default">OK</Badge>;
  if (value > 0) return <Badge variant="destructive">Over-debited +{formatCost(abs)}</Badge>;
  return <Badge variant="secondary">Under-debited -{formatCost(abs)}</Badge>;
}

export default function UserCostAuditPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [data, setData] = useState<AdminUserCostAuditResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAudit = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`users/${userId}/cost-audit`);
      if (!res.ok) throw new Error(`Audit failed (${res.status})`);
      setData((await res.json()) as AdminUserCostAuditResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runAudit();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <LoadingOverlay visible={loading} message="Running cost audit..." />
      <div className="space-y-4 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: userId ?? 'User', to: `/users/${userId}` },
            { label: 'Cost Audit' },
          ]}
        />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Cost audit</h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{userId}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={runAudit} disabled={loading}>
              {loading ? 'Running…' : 'Re-run audit'}
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/users/${userId}/payments`}>Payments →</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/users/${userId}/logs`}>Logs →</Link>
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Discrepancy Summary */}
            <Card className={Math.abs(data.discrepancy.totalDebitedVsExecutions) > 0.001 ? 'border-destructive' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Discrepancy analysis</CardTitle>
                  <DiscrepancyBadge value={data.discrepancy.totalDebitedVsExecutions} />
                </div>
                <CardDescription>{data.discrepancy.note}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <div className="text-sm text-muted-foreground">Monthly debited</div>
                    <div className="mt-1 text-2xl font-semibold font-mono">
                      {formatCost(data.creditState.monthlyCost)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      limit: {formatCost(data.creditState.totalMonthlyLimit)}
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <div className="text-sm text-muted-foreground">Pack debited</div>
                    <div className="mt-1 text-2xl font-semibold font-mono">
                      {formatCost(data.creditState.packCreditsCost)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      limit: {formatCost(data.creditState.packCreditsLimit)}
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <div className="text-sm text-muted-foreground">Sum of AI executions</div>
                    <div className="mt-1 text-2xl font-semibold font-mono">
                      {formatCost(data.executionCostSummary.sumOfExecutionCosts)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {data.executionCostSummary.executionCount} records
                      {data.executionCostSummary.isPartial && ' (partial scan)'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 text-sm">
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <span className="text-muted-foreground">Monthly debited − execution sum</span>
                    <span className={`font-mono ${Math.abs(data.discrepancy.monthlyDebitedVsExecutions) > 0.001 ? 'text-destructive font-bold' : ''}`}>
                      {data.discrepancy.monthlyDebitedVsExecutions >= 0 ? '+' : ''}
                      {formatCost(data.discrepancy.monthlyDebitedVsExecutions)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <span className="text-muted-foreground">Total debited (monthly + pack) − execution sum</span>
                    <span className={`font-mono ${Math.abs(data.discrepancy.totalDebitedVsExecutions) > 0.001 ? 'text-destructive font-bold' : ''}`}>
                      {data.discrepancy.totalDebitedVsExecutions >= 0 ? '+' : ''}
                      {formatCost(data.discrepancy.totalDebitedVsExecutions)}
                    </span>
                  </div>
                </div>

                {data.executionCostSummary.isPartial && (
                  <div className="mt-3 rounded-md border border-yellow-500/50 bg-yellow-500/5 p-3 text-sm text-yellow-700">
                    Partial scan: {data.executionCostSummary.pagesScanned} pages of 100 records scanned.
                    Navigate to <Link to={`/users/${userId}/ai-executions`} className="underline">AI executions</Link> to see all records.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Credit State */}
            <Card>
              <CardHeader>
                <CardTitle>Live credit state (users-table)</CardTitle>
                <CardDescription>
                  Current billing period ·{' '}
                  {data.creditState.subscriptionPeriodEnd
                    ? `renews ${formatTimestamp(data.creditState.subscriptionPeriodEnd)}`
                    : 'no subscription'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                  {[
                    { label: 'Subscription', value: data.creditState.subscription ?? '—' },
                    { label: 'Monthly cost', value: formatCost(data.creditState.monthlyCost) },
                    { label: 'Monthly limit', value: formatCost(data.creditState.monthlyLimit) },
                    { label: 'Previous unused spend', value: formatCost(data.creditState.previousUnusedSpend) },
                    { label: 'Total monthly limit', value: formatCost(data.creditState.totalMonthlyLimit) },
                    { label: 'Monthly remaining', value: formatCost(data.creditState.monthlyRemaining) },
                    { label: 'Pack cost', value: formatCost(data.creditState.packCreditsCost) },
                    { label: 'Pack limit', value: formatCost(data.creditState.packCreditsLimit) },
                    { label: 'Pack remaining', value: formatCost(data.creditState.packCreditsRemaining) },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex flex-col">
                      <span className="text-sm font-medium text-muted-foreground">{label}</span>
                      <span className="text-base font-mono font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Recent payments</CardTitle>
                <CardDescription>
                  {data.paymentSummary.recentPayments.length} recent events ·{' '}
                  {data.paymentSummary.totalCreditsAdded.toLocaleString()} total credits added ·{' '}
                  ${(data.paymentSummary.totalAmountPaid / 100).toFixed(2)} total paid
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.paymentSummary.recentPayments.length > 0 ? (
                  <div className="overflow-hidden rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Event</TableHead>
                          <TableHead className="text-right">Credits</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Description</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.paymentSummary.recentPayments.map((p, idx) => (
                          <TableRow key={`${p.created}-${idx}`}>
                            <TableCell className="font-mono text-xs">{formatTimestamp(p.created)}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{p.eventCode.replace(/_/g, ' ')}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {p.credits != null ? p.credits.toLocaleString() : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {p.amountPaid != null ? `$${(p.amountPaid / 100).toFixed(2)}` : '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {p.description ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No payment events found.</p>
                )}
                <div className="mt-2 text-sm">
                  <Link to={`/users/${userId}/payments`} className="text-primary hover:underline">
                    View full payment history →
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Top Executions */}
            {data.topExecutions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Top cost executions (ai-exec-table)</CardTitle>
                  <CardDescription>
                    Highest-cost AI calls — sorted by callCost descending
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-hidden rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Execution ID</TableHead>
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Tokens In</TableHead>
                          <TableHead className="text-right">Tokens Out</TableHead>
                          <TableHead className="text-right">Cached</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.topExecutions.map((exec, idx) => (
                          <TableRow key={exec.id ?? idx}>
                            <TableCell className="font-mono text-xs">{exec.id ?? '—'}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {formatTimestamp(exec.timestamp)}
                            </TableCell>
                            <TableCell className="text-xs">{exec.modelId ?? '—'}</TableCell>
                            <TableCell className="text-right font-mono font-bold">
                              {formatCost(exec.cost)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {exec.tokensIn != null ? formatNumber(exec.tokensIn) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {exec.tokensOut != null ? formatNumber(exec.tokensOut) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {exec.tokensCached != null ? formatNumber(exec.tokensCached) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-2 text-sm">
                    <Link to={`/users/${userId}/ai-executions`} className="text-primary hover:underline">
                      View full execution history →
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            <p className="text-xs text-muted-foreground">
              Generated at {data.generatedAt}
            </p>
          </>
        )}
      </div>
    </>
  );
}
